import { Document, Image, Page, StyleSheet, View, Text } from "@react-pdf/renderer"
import React from "react"
import { formatDateToView } from "../../../functions/DateTime"
import { getFeedbackGradeCategories } from "../../../functions/FilterCards"
import { getFacilitator, getParticipants, getRequester } from "../../../functions/FilterUsers"
import logoFull from "../../../images/logo-full.png"
import { getCategoryLabel } from "../svg/SvgCanvas/utils/PostitUtils"
import CategoryTextList from "./components/CategoryTextList"
import LabeledText from "./components/LabeledText"
import { getLevels} from "../svg/SvgCanvas/quadrants/utils/evaluationLevel"
import { CountEvaluationsLevels } from "./components/CountEvaluationsLevels"
import { t } from "i18next"

const COLORS = {
  background: "#F7F8FA",
  textPrimary: "#1F2937",
  textSecondary: "#6B7280",
  brand: "#0EA5E9",
  brandDark: "#0284C7",
  border: "#E5E7EB",
  divider: "#D1D5DB",
  cardBg: "#FFFFFF",
}

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 11,
    backgroundColor: COLORS.background,
    color: COLORS.textPrimary,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 18,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: COLORS.brand,
  },
  headerLeft: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.brandDark,
  },
  headerSubtitle: {
    fontSize: 10,
    color: COLORS.textSecondary,
  },
  section: {
    marginBottom: 14,
  },
  sectionCard: {
    backgroundColor: COLORS.cardBg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 14,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  divider: {
    height: 1,
    backgroundColor: COLORS.divider,
    marginVertical: 10,
  },
  infoGrid: {
    display: "flex",
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  infoCol: {
    flexGrow: 1,
    flexBasis: "48%",
  },
  exception: {
    marginTop: 6,
  },
})

interface Props {
  feedback: IFeedback
  cards: ICard[]
}

const GeneratePDF: React.FC<Props> = ({ feedback, cards }) => {
  const users = feedback?.users ?? []
  const gradeFeedback: ICard[] = getFeedbackGradeCategories(cards) as ICard[]
  const requesterGradeCategory = gradeFeedback.filter((card) => card.owner === getRequester(users)?.cognito_id).map((card) => card.category)
  const participantsGradeCategory = gradeFeedback.filter((card) => card.owner !== getRequester(users)?.cognito_id)
  const levels = getLevels(feedback.evaluation_level)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image src={logoFull} style={{ width: 120 }} />
          </View>
          <View>
            <Text style={styles.headerTitle}>{t('feedback.report.title')}</Text>
            <Text style={styles.headerSubtitle}>{formatDateToView(feedback.schedule_date as string)}</Text>
          </View>
        </View>

        {/* Informações principais */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('feedback.report.information')}</Text>
            {feedback && (
              <View style={styles.infoGrid}>
                <View style={styles.infoCol}>
                  <LabeledText label={t('feedback.roles.requester')} value={getRequester(users)?.name || t('feedback.notInformed')} />
                </View>
                <View style={styles.infoCol}>
                  <LabeledText label={t('feedbackRoles.facilitator')} value={getFacilitator(users)?.name || t('feedback.notInformed')} />
                </View>
                <View style={{ width: "100%" }}>
                  <LabeledText
                    label={t('feedback.roles.participant')}
                    value={
                      getParticipants(users)
                        .map((user) => user.name)
                        .filter(Boolean)
                        .join(", ") || t('feedback.notInformed')
                    }
                  />
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Papéis */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('board.title.roles')}</Text>
            <CategoryTextList cards={cards} category="roles" type="list" />
          </View>
        </View>

        {/* Atividades */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('board.title.activities')}</Text>
            <CategoryTextList cards={cards} category="activities" type="list" />
          </View>
        </View>

        {/* Resultados Esperados */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('board.title.expected-results')}</Text>
            <CategoryTextList cards={cards} category="expected-results" type="list" />
          </View>
        </View>

        {/* Autoavaliação */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <View style={styles.exception}>
              <LabeledText
                label={t('feedback.evaluation.self')}
                value={getCategoryLabel(requesterGradeCategory[0] as string, levels as ILevel)}
              />
            </View>
          </View>
        </View>

        {/* Avaliações dos Participantes */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('feedback.evaluation.title')}</Text>
            <CountEvaluationsLevels
              cards={participantsGradeCategory}
              category={Object.keys(levels).map((key) => key)}
              title={t('feedback.evaluation.title')}
              levels={levels}
            />
          </View>
        </View>

        {/* Pontos Positivos */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('board.title.positive-points')}</Text>
            <CategoryTextList cards={cards} category="positive-points" type="list" />
          </View>
        </View>

        {/* Pontos Negativos */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('board.title.negative-points')}</Text>
            <CategoryTextList cards={cards} category="negative-points" type="list" />
          </View>
        </View>

        {/* Ações de Melhoria */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('board.title.improvement-actions')}</Text>
            <CategoryTextList cards={cards} category="improvement-actions" type="list" />
          </View>
        </View>

        {/* Sentimentos */}
        <View style={styles.section}>
          <View style={styles.sectionCard} wrap={false}>
            <Text style={styles.sectionTitle}>{t('board.title.feelings')}</Text>
            <CategoryTextList cards={cards} category="feelings" type="list" />
          </View>
        </View>
      </Page>
    </Document>
  )
}

export default GeneratePDF
